/**
 * The free trial a new account is offered, as far as this app can speak for it.
 *
 * ## What is real here, and what is not
 *
 * The trial itself is the backend's now: `POST /billing/trial/` starts one and
 * answers with its dates, and `GET /billing/subscription/` reports it — see
 * `trialService`. **Nothing in this file is an entitlement**, and nothing reads
 * it to decide whether the product is open; that is `useSubscriptionGate`, off
 * the server's own answer.
 *
 * What is left here is two much smaller jobs:
 *
 *   - `trialEndOf` and `trialReminderOf` date the summary the reader is shown
 *     *before* they press the button. There is no trial to ask about yet, so
 *     those two dates are this app's arithmetic — a fair statement of what the
 *     server will say, replaced by what it actually says the moment it says it.
 *   - the stash carries the started trial across one navigation, so the
 *     confirmation screen can name the plan and the day it ends without asking
 *     again, and can survive a reload on the way.
 *
 * The dates in the stash are the server's, not these. See `handleStart`.
 *
 * `sessionStorage`, like `checkoutOutcome` beside it and for the same reason:
 * this is one sign-up in one tab, carried across a navigation, and it has no
 * business outliving the tab that started it.
 */

import type { BillingCycleId } from "./types/billing.types";

/** How long the trial runs, as every screen in the flow says it. */
export const TRIAL_DAYS = 14;

/** How long before it ends the reminder goes out — what the summary's middle
 *  milestone promises. Copy: this app does not send the mail. */
export const TRIAL_REMINDER_DAYS = 3;

/** The same clock time, a number of days later. Negative counts backwards,
 *  which is how the reminder is dated off the end. */
const daysAfter = (from: Date, days: number): Date => {
  const date = new Date(from);
  date.setDate(date.getDate() + days);
  return date;
};

/**
 * The last day of a trial started on `from` — the fourteenth of fourteen.
 *
 * Counted the way a recharge counts validity: **the day it starts is day one**.
 * A trial taken on the 21st is therefore good through the 3rd, not the 4th, and
 * `from + TRIAL_DAYS` would be a fifteenth day nobody was promised. The charge
 * falls the morning after; that day is `trialBillingOf`, and the two are said
 * separately on the summary because they are two different facts.
 */
export const trialEndOf = (from: Date = new Date()): Date =>
  daysAfter(from, TRIAL_DAYS - 1);

/** The day the first charge falls: the day after the trial's last one. Also
 *  what the server's `ends_at` is — the instant the fortnight stops. */
export const trialBillingOf = (endsOn: Date): Date => daysAfter(endsOn, 1);

/** The last day of a trial the *server* dated, whose `ends_at` is the moment it
 *  runs out rather than the last day it covers. The inverse of the above, and
 *  what turns the backend's instant into the date a screen can print. */
export const trialLastDayOf = (chargedOn: Date): Date =>
  daysAfter(chargedOn, -1);

/** The day the "your trial is ending" mail is promised for — three days before
 *  the last one, which is day eleven of the fourteen. */
export const trialReminderOf = (endsOn: Date): Date =>
  daysAfter(endsOn, -TRIAL_REMINDER_DAYS);

/** What the confirmation screen has to be able to say, handed to it by the
 *  details screen across a navigation it may well be reloaded after. */
export interface StartedTrial {
  cycle: BillingCycleId;
  /** ISO instants, so a reload reads back the same two dates rather than
   *  recomputing them off a clock that has since passed midnight. */
  startedOn: string;
  endsOn: string;
}

const key = (cycle: BillingCycleId) => `naurist.trial.started.${cycle}`;

const isStarted = (value: unknown): value is StartedTrial => {
  const trial = value as StartedTrial | null;
  return (
    !!trial &&
    typeof trial === "object" &&
    (trial.cycle === "monthly" || trial.cycle === "yearly") &&
    typeof trial.startedOn === "string" &&
    typeof trial.endsOn === "string" &&
    !Number.isNaN(new Date(trial.endsOn).getTime())
  );
};

/**
 * Every read and write is defended. Session storage can be full, switched off
 * by policy, or holding something an older build wrote — none of which is a
 * reason for a trial that has just been started to fail to be confirmed. A bad
 * read is simply "no trial", which sends the reader back to the plan.
 */
export const stashTrial = (trial: StartedTrial): void => {
  try {
    window.sessionStorage.setItem(key(trial.cycle), JSON.stringify(trial));
  } catch {
    // A trial that cannot be remembered still confirms, as long as the page is
    // not reloaded. Not worth stopping a sign-up over.
  }
};

export const readTrial = (cycle: BillingCycleId): StartedTrial | null => {
  try {
    const raw = window.sessionStorage.getItem(key(cycle));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isStarted(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** The cycle a `:cycle` param names, or `undefined` for a word that names
 *  neither — a URL typed by hand, or a cycle that has stopped being sold. */
export const cycleParam = (
  value: string | undefined
): BillingCycleId | undefined =>
  value === "monthly" || value === "yearly" ? value : undefined;
